import { useEffect, useMemo, useRef, useState } from 'react'
import { AxiosError } from 'axios'
import { customerRegistryApi } from '../../api/customerRegistry'
import { emptyCustomerYaml, type CustomerYaml } from '../../types/customer'

type Mode = 'create' | 'edit'

interface CustomerFormModalProps {
  mode: Mode
  open: boolean
  initial?: CustomerYaml | null
  onClose: () => void
  onSaved: (saved: CustomerYaml) => void
}

interface FieldErrors {
  [key: string]: string | undefined
}

const YYYY_MM_DD = /^\d{4}-\d{2}-\d{2}$/
const DATE_OR_DASH = /^(\d{4}-\d{2}-\d{2}|-)?$/

export function CustomerFormModal({ mode, open, initial, onClose, onSaved }: CustomerFormModalProps) {
  const [form, setForm] = useState<CustomerYaml>(() => initial ?? emptyCustomerYaml())
  const [errors, setErrors] = useState<FieldErrors>({})
  const [submitting, setSubmitting] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const firstInputRef = useRef<HTMLInputElement | null>(null)

  // Reset form on open / initial change
  useEffect(() => {
    if (open) {
      setForm(initial ? structuredClone(initial) : emptyCustomerYaml())
      setErrors({})
      setServerError(null)
      // focus first input next tick
      setTimeout(() => firstInputRef.current?.focus(), 50)
    }
  }, [open, initial])

  // Escape closes
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, submitting, onClose])

  const title = mode === 'create' ? '고객 등록' : '고객 정보 수정'
  const subtitle = useMemo(
    () =>
      mode === 'create'
        ? 'OCI 고객사를 customers.yml에 추가합니다. 모든 필드는 수집·분석에 바로 반영됩니다.'
        : '고객사 정보를 수정합니다. 저장 즉시 잔액·계약 계산에 반영됩니다.',
    [mode],
  )

  const setField = <K extends keyof CustomerYaml>(key: K, value: CustomerYaml[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }
  const setBalance = <K extends keyof CustomerYaml['balance']>(key: K, value: CustomerYaml['balance'][K]) => {
    setForm((prev) => ({ ...prev, balance: { ...prev.balance, [key]: value } }))
  }
  const setContract = <K extends keyof CustomerYaml['contract']>(key: K, value: CustomerYaml['contract'][K]) => {
    setForm((prev) => ({ ...prev, contract: { ...prev.contract, [key]: value } }))
  }
  const setDetails = <K extends keyof CustomerYaml['details']>(key: K, value: CustomerYaml['details'][K]) => {
    setForm((prev) => ({ ...prev, details: { ...prev.details, [key]: value } }))
  }

  const validate = (): boolean => {
    const next: FieldErrors = {}
    if (!form.profile.trim()) next['profile'] = 'profile을 입력하세요'
    else if (!/^[A-Za-z0-9_.-]+$/.test(form.profile))
      next['profile'] = '영문/숫자/._- 문자만 허용됩니다'
    if (!form.name.trim()) next['name'] = '고객명을 입력하세요'
    if (!form.tenancy.trim()) next['tenancy'] = 'tenancy를 입력하세요'

    if (form.balance.initial === null || Number.isNaN(Number(form.balance.initial)))
      next['balance.initial'] = '잔액 초기값을 입력하세요'
    else if (Number(form.balance.initial) < 0) next['balance.initial'] = '0 이상이어야 합니다'

    if (!form.balance.asOfDate.trim()) next['balance.asOfDate'] = '기준일을 입력하세요'
    else if (!YYYY_MM_DD.test(form.balance.asOfDate)) next['balance.asOfDate'] = 'YYYY-MM-DD 형식'

    if (!form.balance.currency.trim()) next['balance.currency'] = '통화를 입력하세요'

    if (form.contract.amount === null || Number.isNaN(Number(form.contract.amount)))
      next['contract.amount'] = '계약 금액을 입력하세요'
    else if (Number(form.contract.amount) < 0) next['contract.amount'] = '0 이상이어야 합니다'

    if (!DATE_OR_DASH.test(form.contract.startDate))
      next['contract.startDate'] = "YYYY-MM-DD 또는 '-' 입력"
    if (!DATE_OR_DASH.test(form.contract.endDate))
      next['contract.endDate'] = "YYYY-MM-DD 또는 '-' 입력"

    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting) return
    setServerError(null)
    if (!validate()) return

    const payload: CustomerYaml = {
      ...form,
      profile: form.profile.trim(),
      name: form.name.trim(),
      tenancy: form.tenancy.trim(),
      balance: {
        ...form.balance,
        asOfDate: form.balance.asOfDate.trim(),
        currency: form.balance.currency.trim() || 'KRW',
      },
      contract: {
        ...form.contract,
        startDate: (form.contract.startDate || '-').trim(),
        endDate: (form.contract.endDate || '-').trim(),
      },
      details: {
        csi: form.details.csi?.trim() || '-',
        opportunityId: form.details.opportunityId?.trim() || '-',
        subscriptionPlanNumber: form.details.subscriptionPlanNumber?.trim() || '-',
        salesRepresentative: form.details.salesRepresentative?.trim() || '-',
        cloudAccountManager: form.details.cloudAccountManager?.trim() || '-',
      },
    }

    setSubmitting(true)
    try {
      const saved =
        mode === 'create'
          ? await customerRegistryApi.create(payload)
          : await customerRegistryApi.update(initial!.profile, payload)
      onSaved(saved)
    } catch (err) {
      if (err instanceof AxiosError) {
        const data = err.response?.data as { message?: string; errorCode?: string; details?: Record<string, string> }
        if (data?.details) {
          // Map server-side validation errors to field errors
          const mapped: FieldErrors = {}
          for (const [k, v] of Object.entries(data.details)) {
            mapped[k] = String(v)
          }
          setErrors(mapped)
          setServerError(data.message || '입력값을 확인해 주세요.')
        } else {
          setServerError(data?.message || err.message)
        }
      } else {
        setServerError('저장에 실패했습니다. 잠시 후 다시 시도해주세요.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/50 dark:bg-black/70 backdrop-blur-[2px]"
      aria-modal="true"
      role="dialog"
      aria-labelledby="customer-form-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose()
      }}
    >
      <div className="w-full sm:max-w-[560px] max-h-[95vh] bg-white dark:bg-gray-900 rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col animate-tossIn">
        {/* Header */}
        <div className="px-6 sm:px-8 pt-7 pb-5 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-900 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2
                id="customer-form-title"
                className="text-2xl font-bold text-gray-900 dark:text-gray-50 tracking-tight"
              >
                {title}
              </h2>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{subtitle}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="shrink-0 w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-300 transition-colors disabled:opacity-50"
              aria-label="닫기"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 sm:px-8 pt-6 pb-4 space-y-8">
          <Section title="기본 정보" description="OCI 테넌시 식별에 사용되는 핵심 값입니다.">
            <Field
              label="profile"
              hint="collector가 OCI config에서 찾는 프로필명. 영문·숫자·. _ - 만 허용."
              error={errors['profile']}
            >
              <input
                ref={firstInputRef}
                className={inputClass(errors['profile'])}
                value={form.profile}
                onChange={(e) => setField('profile', e.target.value)}
                placeholder="예) wizbase"
                autoComplete="off"
                disabled={mode === 'edit' && submitting}
              />
            </Field>
            <Field label="고객명 (name)" hint="사이드바·대시보드에 표시되는 한글/영문명." error={errors['name']}>
              <input
                className={inputClass(errors['name'])}
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                placeholder="예) 위즈베이스"
                autoComplete="off"
              />
            </Field>
            <Field label="tenancy" hint="OCI tenancy name. OCI 관리 콘솔 > Profile > Tenancy." error={errors['tenancy']}>
              <input
                className={inputClass(errors['tenancy'])}
                value={form.tenancy}
                onChange={(e) => setField('tenancy', e.target.value)}
                placeholder="예) wizbase"
                autoComplete="off"
              />
            </Field>
          </Section>

          <Section title="잔액 정보" description="기준일 기준의 초기 잔액입니다. 이후 비용은 누적 차감됩니다.">
            <div className="grid grid-cols-2 gap-4">
              <Field label="초기 잔액 (initial)" error={errors['balance.initial']}>
                <NumberInput
                  value={form.balance.initial}
                  onChange={(v) => setBalance('initial', v)}
                  placeholder="예) 3,991,550"
                  aria-invalid={!!errors['balance.initial']}
                  className={inputClass(errors['balance.initial'])}
                />
              </Field>
              <Field label="통화 (currency)" error={errors['balance.currency']}>
                <input
                  className={inputClass(errors['balance.currency'])}
                  value={form.balance.currency}
                  onChange={(e) => setBalance('currency', e.target.value.toUpperCase())}
                  placeholder="KRW"
                  maxLength={8}
                />
              </Field>
            </div>
            <Field label="기준일 (as_of_date)" hint="YYYY-MM-DD" error={errors['balance.asOfDate']}>
              <input
                type="date"
                className={inputClass(errors['balance.asOfDate'])}
                value={form.balance.asOfDate}
                onChange={(e) => setBalance('asOfDate', e.target.value)}
              />
            </Field>
          </Section>

          <Section title="계약 정보" description="계약 금액과 기간. 기간을 모르면 '-'로 남겨두세요.">
            <Field label="계약 금액 (amount)" error={errors['contract.amount']}>
              <NumberInput
                value={form.contract.amount}
                onChange={(v) => setContract('amount', v)}
                placeholder="예) 5,912,874"
                className={inputClass(errors['contract.amount'])}
              />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="시작일 (start_date)" hint="YYYY-MM-DD 또는 '-'" error={errors['contract.startDate']}>
                <DashableDateInput
                  value={form.contract.startDate}
                  onChange={(v) => setContract('startDate', v)}
                  invalid={!!errors['contract.startDate']}
                />
              </Field>
              <Field label="종료일 (end_date)" hint="YYYY-MM-DD 또는 '-'" error={errors['contract.endDate']}>
                <DashableDateInput
                  value={form.contract.endDate}
                  onChange={(v) => setContract('endDate', v)}
                  invalid={!!errors['contract.endDate']}
                />
              </Field>
            </div>
          </Section>

          <Section title="세부 정보" description="영업·Oracle 계약 식별정보. 비워두면 '-'로 저장됩니다.">
            <Field label="CSI">
              <input
                className={inputClass()}
                value={form.details.csi ?? ''}
                onChange={(e) => setDetails('csi', e.target.value)}
                placeholder="예) 16616094"
              />
            </Field>
            <Field label="Opportunity ID">
              <input
                className={inputClass()}
                value={form.details.opportunityId ?? ''}
                onChange={(e) => setDetails('opportunityId', e.target.value)}
                placeholder="예) A7ZHVV"
              />
            </Field>
            <Field label="Subscription Plan Number">
              <input
                className={inputClass()}
                value={form.details.subscriptionPlanNumber ?? ''}
                onChange={(e) => setDetails('subscriptionPlanNumber', e.target.value)}
                placeholder="예) 11786558"
              />
            </Field>
            <Field label="영업대표 (sales_representative)">
              <input
                className={inputClass()}
                value={form.details.salesRepresentative ?? ''}
                onChange={(e) => setDetails('salesRepresentative', e.target.value)}
                placeholder="예) Juhyung Kang (JUHYUNG.KANG@ORACLE.COM)"
              />
            </Field>
            <Field label="클라우드 계정 관리자 (cloud_account_manager)">
              <input
                className={inputClass()}
                value={form.details.cloudAccountManager ?? ''}
                onChange={(e) => setDetails('cloudAccountManager', e.target.value)}
                placeholder="예) 홍길동 (gdhong@example.com)"
              />
            </Field>
          </Section>

          {serverError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-xl px-4 py-3 text-sm text-red-700 dark:text-red-300">
              {serverError}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="px-6 sm:px-8 py-4 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 sm:flex-none sm:px-6 py-3 rounded-2xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-semibold hover:bg-gray-200 dark:hover:bg-gray-700 transition disabled:opacity-50"
          >
            취소
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-3 rounded-2xl bg-gradient-to-r from-primary-700 to-primary-600 dark:from-primary-800 dark:to-primary-700 text-white font-semibold shadow-lg shadow-primary-700/20 hover:from-primary-600 hover:to-primary-500 disabled:opacity-60 transition-all"
          >
            {submitting ? '저장 중…' : mode === 'create' ? '등록하기' : '저장하기'}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes tossIn {
          0% { opacity: 0; transform: translateY(24px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-tossIn { animation: tossIn 0.25s cubic-bezier(0.22, 1, 0.36, 1); }
      `}</style>
    </div>
  )
}

function Section({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <section>
      <header className="mb-3">
        <h3 className="text-sm font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">{title}</h3>
        {description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{description}</p>}
      </header>
      <div className="space-y-4">{children}</div>
    </section>
  )
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string
  hint?: string
  error?: string
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{label}</span>
        {hint && !error && <span className="text-[11px] text-gray-400 dark:text-gray-500">{hint}</span>}
      </div>
      {children}
      {error && (
        <p className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}
    </label>
  )
}

function inputClass(error?: string) {
  const base =
    'w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-800 border text-gray-900 dark:text-gray-50 placeholder:text-gray-400 dark:placeholder:text-gray-500 transition-colors focus:outline-none focus:bg-white dark:focus:bg-gray-900'
  const normal = 'border-gray-200 dark:border-gray-700 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/10'
  const invalid = 'border-red-400 dark:border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/10'
  return `${base} ${error ? invalid : normal}`
}

/**
 * 금액 입력 컴포넌트.
 * 사용자 입력은 자유 형식(쉼표 허용)으로 받고 state는 number | null을 유지한다.
 */
function NumberInput({
  value,
  onChange,
  placeholder,
  className,
}: {
  value: number | null
  onChange: (v: number | null) => void
  placeholder?: string
  className?: string
  ['aria-invalid']?: boolean
}) {
  const display = value === null || Number.isNaN(value) ? '' : Number(value).toLocaleString('ko-KR')
  return (
    <input
      type="text"
      inputMode="numeric"
      className={className}
      value={display}
      onChange={(e) => {
        const raw = e.target.value.replace(/[^0-9.-]/g, '')
        if (raw === '' || raw === '-') return onChange(null)
        const num = Number(raw)
        onChange(Number.isNaN(num) ? null : num)
      }}
      placeholder={placeholder}
    />
  )
}

/**
 * 날짜 또는 '-' 입력. 기본은 date picker. 비어있거나 '-'는 수동 토글로 표시.
 */
function DashableDateInput({
  value,
  onChange,
  invalid,
}: {
  value: string
  onChange: (v: string) => void
  invalid?: boolean
}) {
  const isDash = value === '-' || value === ''
  return (
    <div className="flex items-stretch gap-2">
      {isDash ? (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass(invalid ? 'err' : undefined)}
          placeholder="-"
        />
      ) : (
        <input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass(invalid ? 'err' : undefined)}
        />
      )}
      <button
        type="button"
        onClick={() => onChange(isDash ? new Date().toISOString().slice(0, 10) : '-')}
        className="shrink-0 px-3 rounded-xl text-xs font-semibold bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
        title={isDash ? '날짜 입력으로 전환' : "'-'로 저장"}
      >
        {isDash ? '날짜' : '미정'}
      </button>
    </div>
  )
}
